import {createSelector} from 'reselect';
import LocationalState, {
    LocationalConfidence,
    LocationalRoadObject,
    LocationalRoadObjectType,
    SelectedLocalizationType
} from "./LocationalState";
import {dataforvalterMapConfig} from "../../map/map-config";

const stateRoadObjectTypes = (state: LocationalState) => state.roadObjectTypes;
const stateRoadObjects = (state: LocationalState) => state.roadObjects;
const stateSelectedRoadObjectIds = (state: LocationalState) => state.selectedRoadObjects;
const stateSelectedTypes = (state: LocationalState) => state.selectedTypes;
const stateRoadSegments = (state: LocationalState) => state.roadSegments;
const stateSelectedRoadSegment = (state: LocationalState) => state.selectedRoadSegment;
const stateProgressGoal = (state: LocationalState) => state.progressGoal;
const stateProgress = (state: LocationalState) => state.progress;
const stateActiveConfidenceFilters = (state: LocationalState) => state.activeConfidenceFilters;
const stateDisplaySynchronizedObjects = (state: LocationalState) => state.displaySynchronizedObjects;
const stateUseAnchors = (state: LocationalState) => state.useAnchors;
const stateListenForAnchorPoint = (state: LocationalState) => state.listenForAnchorPoint;
const stateStartAnchor = (state: LocationalState) => state.startAnchor;
const stateEndAnchor = (state: LocationalState) => state.endAnchor;
const stateUserSession = (state: LocationalState) => state.user;
const stateShowAutoLocalizeModal = (state: LocationalState) => state.showAutoLocalizeModal;
const stateDetailsModalFeatureId = (state: LocationalState) => state.detailsModalFeatureId;
const stateDatacatalog = (state: LocationalState) => state.datacatalog;
export const fetchingPointsFromReference = (state: LocationalState) => state.fetchingPointsFromReference;
const stateLocationalSelectedForLaneModal = (state: LocationalState) => state.locationalSelectedForLaneModal;
export const selectedOverwriteRoadCategory = (state: LocationalState) => state.selectedOverwriteRoadCategory;
export const selectedOverwriteRoadUserGroup = (state: LocationalState) => state.selectedOverwriteRoadUserGroup;

function deepCopy<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
}

export const useAnchors = createSelector(
    [stateUseAnchors], (useAnchors) => {
        return useAnchors;
    }
);

export const listenForAnchorPoint = createSelector(
    [stateListenForAnchorPoint], (listenForAnchorPoint) => {
        return listenForAnchorPoint;
    }
);

export const startAnchor = createSelector(
    [stateStartAnchor, useAnchors], (startAnchor, useAnchors) => {
        return useAnchors ? startAnchor : null;
    }
);

export const endAnchor = createSelector(
    [stateEndAnchor, useAnchors], (endAnchor, useAnchors) => {
        return useAnchors ? endAnchor : null;
    }
);

const activeConfidenceFilters = createSelector(
    [stateActiveConfidenceFilters], (filters) => {
        return filters;
    }
);

export const showConfidenceMissing = createSelector(
    [activeConfidenceFilters], (filters) => {
        return filters.some(f => f === LocationalConfidence.missing);
    }
);

export const showConfidenceConfident = createSelector(
    [activeConfidenceFilters], (filters) => {
        return filters.some(f => f === LocationalConfidence.confident);
    }
);

export const showConfidenceUncertain = createSelector(
    [activeConfidenceFilters], (filters) => {
        return filters.some(f => f === LocationalConfidence.uncertain);
    }
);
export const showSynchronizedObjects = createSelector(
    [stateDisplaySynchronizedObjects], (displaySynchronizedObjects:boolean) => {
        return displaySynchronizedObjects;
    }
);
export const startAnchorIsSet = createSelector(
    [stateStartAnchor], (startAnchor) => {
        return startAnchor !== null;
    }
);
export const endAnchorIsSet = createSelector(
    [stateEndAnchor], (endAnchor) => {
        return endAnchor !== null;
    }
);

export const selectedRoadObjectIds = createSelector(
    [stateSelectedRoadObjectIds], (roadObjectsIds) => {
        return roadObjectsIds;
    }
);

export const locationalSelectedForLaneModal = createSelector(
    [stateLocationalSelectedForLaneModal], (locational) => {
        return locational;
    }
)

export const locationalRoadObjectTypes = createSelector(
    [stateRoadObjectTypes], (types) => {
        return deepCopy(types) as LocationalRoadObjectType[];
    }
);

export const locationalRoadObjects = createSelector(
    [stateRoadObjects],
    (roadObjects) => {
        let ros = deepCopy(roadObjects) as LocationalRoadObject[];
        return ros.map(ro => {
            let locationalConfidence = !ro.locational ? LocationalConfidence.missing
                : ro.locational.confidence < dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD ? LocationalConfidence.uncertain
                    : LocationalConfidence.confident;
            ro.locationalConfidence = locationalConfidence;
            return ro;
        })

    }
);

export const disableDescriptiveName = createSelector(
    [stateUserSession], (user) => {
        if (!user) {
            return false;
        }
        return user.featureEnabled("DisableDescriptiveFeatureNameInAssociationAndLocationTabs");
    }
)

const sortedRoadObjects = createSelector(
    [locationalRoadObjects, disableDescriptiveName],
    (ros, disableDescriptiveName) => {
        type CompareType = (a:LocationalRoadObject, b:LocationalRoadObject) => number;

        const compareRoadObjectNames = (intlCollator:Intl.Collator):CompareType => {
            return function compareWithIntlCollator (a:LocationalRoadObject, b:LocationalRoadObject):number {
                if (disableDescriptiveName) {
                    return intlCollator.compare(a.name, b.name);
                } else {
                    return intlCollator.compare(a.descriptiveName, b.descriptiveName);
                }
            }
        }

        const sortRoadObjects = (ros: LocationalRoadObject[], intlCollator:Intl.Collator):LocationalRoadObject[] => {
            ros.sort(compareRoadObjectNames(intlCollator));
            return ros;
        }

        let intlCollator = Intl.Collator('nb', { numeric: true });

        ros = sortRoadObjects(ros, intlCollator);
        return ros;
    }
);

export const showAutoLocalizeModal = createSelector(
    [stateShowAutoLocalizeModal], (showModal) => {
        return showModal;
    }
);

export const showLocationalDetailsModal = createSelector(
    [stateDetailsModalFeatureId], (featureId) => {
        if (featureId) {
            return true;
        }
        return false;
    }
);

export const featureInDetailsModal = createSelector(
    [stateDetailsModalFeatureId, locationalRoadObjects], (featureId, roadObjects) => {
        return roadObjects.find(ro => ro.id === featureId);
    }
);

export const featureNameInDetailsModal = createSelector(
    [featureInDetailsModal, disableDescriptiveName], (feature, disableDescriptiveName) => {
        return disableDescriptiveName ? feature.name : feature.descriptiveName;
    }
);

export const featureTypeNameInDetailsModal = createSelector(
    [featureInDetailsModal, stateDatacatalog], (feature, datacatalog) => {
        const featureType = datacatalog.getFeatureType(feature.typeId);
        return featureType.name;
    }
);

export const featureInDetailsModalCanHaveSidePosition = createSelector(
    [featureInDetailsModal, stateDatacatalog], (feature, datacatalog) => {
        const featureType = datacatalog.getFeatureType(feature.typeId);
        const sidePositionRelevance = featureType.sidePositionRelevance;
        return sidePositionRelevance === "CAN" || sidePositionRelevance === "MUST";
    }
);

export const featureInDetailsModalCanHaveLanes = createSelector(
    [featureInDetailsModal, stateDatacatalog], (feature, datacatalog) => {
        const featureType = datacatalog.getFeatureType(feature.typeId);
        const laneRelevance = featureType.laneRelevance;
        return laneRelevance === "CAN" || laneRelevance === "MUST";
    }
);

const roadObjectTypesWithStatistics = createSelector(
    [locationalRoadObjectTypes, locationalRoadObjects], (rots, allRos) => {
    return rots.map(rot => {
        let roadObjects = allRos.filter(ro => {
            return ro.typeId === rot.typeid;
        });
        rot.totalNumberOfRoadObjects = roadObjects.length;
        rot.lowestLocationalConfidence =roadObjects.map(ro => {
            return ro.locationalConfidence;
        }).reduce((c1, c2) => {
            return Math.min(c1, c2);
        }, LocationalConfidence.confident);

        return rot;
    })
});

export const selectedRoadObjects = createSelector(
    [selectedRoadObjectIds, locationalRoadObjects],
    (roadObjectsIds, roadObjects) => {
        return roadObjects.filter(ro => {
            return roadObjectsIds.some(id => ro.id === id);
        });
    }
);

export const selectedLocalizationType = createSelector(
    [selectedRoadObjects, stateDatacatalog],
    (selection, datacatalog): SelectedLocalizationType => {
        if (selection.length === 0) {
            return SelectedLocalizationType.NONE;
        }
        const selectedTypes = selection.map(ro => ro.typeId);

        const extentTypes = selectedTypes
            .map(typeId => datacatalog.getFeatureType(typeId))
            .filter(featureType => featureType !== undefined)
            .map(featureType => featureType.extentType);

        if (extentTypes.every(et => et === 'POINT')) {
            return SelectedLocalizationType.POINT;
        } else if (extentTypes.some(et => et === 'POINT')) {
            return SelectedLocalizationType.MIXED;
        } else {
            // Includes TURN, which is a different type entirely, but at least it uses two points for localization
            return SelectedLocalizationType.LINE;
        }
    }
)

export const roadSegments = createSelector(
    [stateRoadSegments], (roadSegments) => {
        return roadSegments;
    }
);

export const selectedRoadSegment = createSelector(
    [stateSelectedRoadSegment], (selectedRoadSegment) => {
        return selectedRoadSegment;
    }
);

export const selectedTypes = createSelector(
    [stateSelectedTypes], (types) => {
        return types;
    }
);

const selectionAwareRoadObjects = createSelector(
    [sortedRoadObjects, selectedRoadObjectIds],(ros, selectedROIds) => {
        return ros.map(roadObject => {
            roadObject.isSelected = selectedROIds.some(selectedId => {
                return selectedId === roadObject.id;
            });
            return roadObject;
        })
    }
);

export const confidenceFilteredRoadObjects = createSelector(
    [selectionAwareRoadObjects, showConfidenceMissing, showConfidenceUncertain, showConfidenceConfident],
    (ros, showMissing, showUncertain, showConfident, ) => {
       return ros.filter(ro => {
           let filter =
               ro.isSelected    // allways include selected ROs
               || showMissing && !ro.locational
               || showUncertain && ro.locational?.confidence < dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD
               || showConfident && ro.locational?.confidence >= dataforvalterMapConfig.LOCALIZATION_OK_THRESHOLD;
           return filter;
           }
       )
    }
);

export const filteredRoadObjects = createSelector(
    [confidenceFilteredRoadObjects], (ros) => {
        return ros;
    }
);

export const selectionAwareRoadObjectTypes = createSelector(
    [roadObjectTypesWithStatistics, selectedTypes], (rots, selectedIds) => {
        return rots.map(rot => {
            rot.isSelected = selectedIds.some(id => {
                return id === rot.typeid;
            });
            return rot;
        })
    }
);

export const processedTypesAndObjects = createSelector(
    [selectionAwareRoadObjectTypes, filteredRoadObjects], (rots, ros) => {

        let processedRots = rots.map(rot => {
            rot.roadObjects = ros.filter(ro => {
                return ro.typeId === rot.typeid;
            });
            return rot;
        });

        return processedRots;
    }
);

export const visibleRoadObjects = createSelector(
    [filteredRoadObjects, selectedTypes], (rois, types) => {
        return rois.filter(ro => types.some(rot => rot === ro.typeId));
    }
);

export const loadingInProgress = createSelector(
    [stateProgress, stateProgressGoal], (progress, goal) => {
        return goal !== progress;
    }
);

export const progress = createSelector(
    [stateProgress, stateProgressGoal], (progress, goal) => {
        return (progress / goal * 100).toFixed(0);
    }
);

export const selectedLocationalsAreApproveable = createSelector(
    [selectedRoadObjects], (selectedRoadObjects) => {
       if (selectedRoadObjects.length === 0) {
           return false;
       }
       return !selectedRoadObjects.some(ro => {
           return ro.locationalConfidence === LocationalConfidence.missing
       })
    }
);

export const selectionIsLocalizable = createSelector(
    [selectedRoadObjects, useAnchors, startAnchorIsSet, endAnchorIsSet, selectedLocalizationType],
    (selectedRoadObjects, useAnchors, startAnchorIsSet, endAnchorIsSet, selectedLocalizationType) => {
        const hasSelectedObjects = selectedRoadObjects.length > 0;

        if (useAnchors) {
            if (selectedLocalizationType === SelectedLocalizationType.POINT) {
                return hasSelectedObjects && startAnchorIsSet;
            } else if (selectedLocalizationType === SelectedLocalizationType.LINE) {
                return hasSelectedObjects && startAnchorIsSet && endAnchorIsSet;
            } else {
                return false;
            }
        } else {
            return hasSelectedObjects;
        }
    }
);

export const useSkrivLocationals = createSelector(
    [stateUserSession], (user) => {
        if (!user) {
            return false;
        }

        return user.featureEnabled("SkrivLocationals");
    }
);

export const selectedRoadObjectIdsInBatches = createSelector(
    [selectedRoadObjectIds], (roadObjectsImmutable) => {
        let roadObjects = roadObjectsImmutable.slice(0);    // avoid mutating original array

        let batchSize = roadObjects.length <= 10 ? 1 : Math.ceil(roadObjects.length / 10);
        let batches: string[][] = [];
        while (roadObjects.length) {
            batches.push(roadObjects.splice(0, batchSize))
        }
        return batches;
    }
)

export const allRoadCategories = createSelector([stateDatacatalog], (catalog) => {
    if(catalog){
        return catalog.getRoadCategories();
    } else {
        return {
            E: 'Europaveg',
            R: 'Riksveg',
            F: 'Fylkesveg',
            K: 'Kommunal veg',
            P: 'Privat veg',
            S: 'Skogsbilveg'
        };
    }
});
